import React, { useEffect, useState } from "react";
import Layout1 from "../../components/layout1";
import SubHeader from "../../components/subHeader";
import { Form } from "antd";
import {
  createUser,
  searchUsers,
  forceDisable2FA,
  generateBackupCodes,
  userPagination,
  userDetails,
  userTaskAssign,
  updateUserApi,
  deleteUserApi,
  suspendUser,
  exportUsers
} from "../../functions/user";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import { allRoles } from "../../functions/role";
import { useDispatch } from "react-redux";
import { loaderReducer } from "../../components/toolkit/loader";
import { useDebounce } from "../../hooks/useDebounce";
import Content from "./content";
import "./index.css";
import BackupCodesModal from "../../components/BackupCodesModal";
import { useCan } from "../../utils/permissions";

const Index = () => {
  const can = useCan();
  const canRead = can("user", "read");
  const canCreate = can("user", "create");
  const canUpdate = can("user", "update");
  const canDelete = can("user", "delete");
  // State management
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState("");
  const [pageC, setPageC] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roles, setRoles] = useState([]);
  
  // Modal states
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [is2FAModalVisible, setIs2FAModalVisible] = useState(false);
  const [isBackupCodesOpen, setIsBackupCodesOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  // Form instances
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const dispatch = useDispatch();
  const cookies = new Cookies();
  const token = cookies.get("token");

  // Fetch all users with pagination
  const fetchUsers = async () => {
    if (!canRead) return;
    setLoading(true);
    dispatch(loaderReducer(true));
    try {
      const res = await userPagination(token, pageC, limit);

      // Handle pagination response structure
      let usersData = [];
      let totalCount = 0;
      
      if (res.data && res.data.technicians) {
        usersData = res.data.technicians;
        totalCount = res.data.total || res.data.technicians.length;
      } else if (res.data && res.data.users) {
        usersData = res.data.users;
        totalCount = res.data.total || res.data.users.length;
      } else if (res.data && Array.isArray(res.data)) {
        usersData = res.data;
        totalCount = res.data.length;
      } else if (Array.isArray(res.data)) {
        usersData = res.data;
        totalCount = res.data.length;
      }

      setUsers(usersData);
      // Only set filteredUsers if there's no active search
      if (!searchData.trim()) {
        setFilteredUsers(usersData);
      }
      setTotalUsers(totalCount);
      setLoading(false);
      dispatch(loaderReducer(false));
    } catch (error) {
      toast.error(error.message || "Failed to fetch users");
      setUsers([]);
      setFilteredUsers([]);
      setTotalUsers(0);
      setLoading(false);
      dispatch(loaderReducer(false));
    }
  };

  const fetchRoles = async () => {
    dispatch(loaderReducer(true));
    try {
      const res = await allRoles(token);
      dispatch(loaderReducer(false));
      setRoles(res.data.roles || []);
    } catch (err) {
      dispatch(loaderReducer(false));
      toast.error(err.response?.data?.message || err.message);
    }
  };

  // Get user details similar to technician page
  const getUserDetails = async (userId) => {
    dispatch(loaderReducer(true));
    try {
      const res = await userDetails(token, userId);
      dispatch(loaderReducer(false));
      return res.data.user;
    } catch (err) {
      dispatch(loaderReducer(false));
      toast.error(err.response?.data?.message || err.message);
      return null;
    }
  };

  // Get user task assignments
  const getUserTasks = async (userId) => {
    dispatch(loaderReducer(true));
    try {
      const res = await userTaskAssign(token, userId);
      dispatch(loaderReducer(false));
      return res.data;
    } catch (err) {
      dispatch(loaderReducer(false));
      toast.error(err.response?.data?.message || err.message);
      return null;
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Search users
  const handleSearchAPI = async (value) => {
    if (!value.trim()) {
      setFilteredUsers(users);
      setTotalUsers(users.length);
      return;
    }

    setLoading(true);
    dispatch(loaderReducer(true));
    try {
      const res = await searchUsers(token, value);
      
      // Handle the actual API response structure
      let searchResults = [];
      if (res.data && Array.isArray(res.data)) {
        searchResults = res.data;
      } else if (res.data && res.data.technicians && Array.isArray(res.data.technicians)) {
        searchResults = res.data.technicians;
      } else if (res.data && res.data.users && Array.isArray(res.data.users)) {
        searchResults = res.data.users;
      } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
        searchResults = res.data.data;
      }
      
      setFilteredUsers(searchResults);
      setTotalUsers(searchResults.length);
      setLoading(false);
      dispatch(loaderReducer(false));
    } catch (error) {
      // Fallback to local search if API fails
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(value.toLowerCase()) ||
        user.email.toLowerCase().includes(value.toLowerCase()) ||
        user.techId.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredUsers(filtered);
      setTotalUsers(filtered.length);
      setLoading(false);
      dispatch(loaderReducer(false));
    }
  };

  // Create user
  const handleCreateUser = async (values) => {
    if (!canCreate) return toast.error("You do not have permission to create users");
    dispatch(loaderReducer(true));
    try {
      const userData = { ...values };
      // Map role ID to role name
      if (userData.role) {
        const selectedRole = roles.find(r => r._id === userData.role);
        if (selectedRole) {
          userData.roleName = selectedRole.name;
        }
      }
      
      await createUser(token, userData);
      toast.success("User created successfully");
      setIsCreateModalVisible(false);
      createForm.resetFields();
      fetchUsers();
      dispatch(loaderReducer(false));
    } catch (error) {
      const errorData = error.response?.data;
    
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        const messages = errorData.errors
          .map(err => {
            return err.messages
              ?.map(msg => msg.replace(/"/g, '')) 
              .join(', ');
          })
          .filter(Boolean)
          .join(', ');
    
        toast.error(messages || errorData.message || "Failed to create user");
      } else {
        toast.error(errorData?.message || "Failed to create user");
      }
      dispatch(loaderReducer(false));
    }
  };

  // Update user
  const handleUpdateUser = async (values) => {
    if (!canUpdate) return toast.error("You do not have permission to update users");
    dispatch(loaderReducer(true));
    try {
      const userData = { ...values };
      // Map role ID to role name
      if (userData.role) {
        const selectedRole = roles.find(r => r._id === userData.role);
        if (selectedRole) {
          userData.roleName = selectedRole.name;
        }
      }
      
      await updateUserApi(token, selectedUser._id, userData);
      toast.success("User updated successfully");
      setIsEditModalVisible(false);
      editForm.resetFields();
      setSelectedUser(null);
      fetchUsers();
      dispatch(loaderReducer(false));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update user");
      dispatch(loaderReducer(false));
    }
  };

  // Delete/Restore user
  const handleDeleteUser = async () => {
    if (!canDelete) return toast.error("You do not have permission to delete users");
    dispatch(loaderReducer(true));
    try {
      // Determine if we're deleting or restoring based on current user state
      const isDeleting = !selectedUser.isDeleted;
      const res=await deleteUserApi(token, selectedUser._id, isDeleting);
      toast.success(res.data.message);
      setIsDeleteModalVisible(false);
      setSelectedUser(null);
      fetchUsers();
      dispatch(loaderReducer(false));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update user");
      dispatch(loaderReducer(false));
    }
  };

  // Toggle 2FA
  const handleToggle2FA = async (enabled) => {
    dispatch(loaderReducer(true));
    try {
      await forceDisable2FA(token, selectedUser._id, enabled);
      toast.success(`2FA ${enabled ? 'enabled' : 'disabled'} successfully`);
      setIs2FAModalVisible(false);
      setSelectedUser(null);
      fetchUsers();
      dispatch(loaderReducer(false));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to toggle 2FA");
      dispatch(loaderReducer(false));
    }
  };

  // Generate backup codes
  const handleGenerateBackupCodes = async () => {
    dispatch(loaderReducer(true));
    try {
      const res = await generateBackupCodes(token, selectedUser._id);
      const codes = res?.data?.data?.backupCodes || res?.data?.backupCodes;
      if (Array.isArray(codes) && codes.length > 0) {
        setBackupCodes(codes);
        setIsBackupCodesOpen(true);
      }
      toast.success(res?.data?.message || "Backup codes generated successfully");
      setIs2FAModalVisible(false);
      dispatch(loaderReducer(false));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate backup codes");
      dispatch(loaderReducer(false));
    }
  };

  // Suspend / Unsuspend user
  const handleSuspendUser = async (targetUser, block) => {
    dispatch(loaderReducer(true));
    try {
      await suspendUser(token, targetUser._id, block);
      toast.success(`User ${block ? 'suspended' : 'unsuspended'} successfully`);
      fetchUsers();
      dispatch(loaderReducer(false));
    } catch (error) {
      dispatch(loaderReducer(false));
      toast.error(error.response?.data?.result || error.response?.data?.message || error.message || "Failed to update suspension");
      throw error;
    }
  };

  // Handle user details click similar to technician page
  const handleUserDetailsClick = async (userId) => {
    dispatch(loaderReducer(true));
    try {
      const userDetails = await getUserDetails(userId);
      if (userDetails) {
        setSelectedUser(userDetails);
        setIsViewModalVisible(true);
      }
      dispatch(loaderReducer(false));
    } catch (error) {
      dispatch(loaderReducer(false));
      toast.error("Failed to load user details");
    }
  };

  // Initialize data
  useEffect(() => {
    fetchUsers();
  }, [token, pageC, limit]);

  // Ensure filteredUsers is set when users are loaded
  useEffect(() => {
    if (users.length > 0 && !searchData.trim()) {
      setFilteredUsers(users);
    }
  }, [users, searchData]);

  // Debounced search function - use only one debounce mechanism
  const handleSearchDebounced = useDebounce((value) => {
    handleSearchAPI(value);
  }, 300);

  // Handle change and trigger debounce
  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchData(value);
    handleSearchDebounced(value);
  };

  // Refresh data function
  const handleRefresh = () => {
    setSearchData("");
    setPageC(1);
    fetchUsers();
  };

  // Export users CSV
  const handleExportData = async () => {
    try {
      const res = await exportUsers(token);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "users.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to export users");
    }
  };

  return (
    <Layout1>
      <SubHeader />
      <Content
        arg={{
          users,
          filteredUsers,
          loading,
          searchData,
          setSearchData,
          handleSearchInput,
          pageC,
          setPageC,
          limit,
          setLimit,
          totalUsers,
          roles,
          selectedUser,
          setSelectedUser,
          isCreateModalVisible,
          setIsCreateModalVisible,
          isEditModalVisible,
          setIsEditModalVisible,
          isViewModalVisible,
          setIsViewModalVisible,
          isDeleteModalVisible,
          setIsDeleteModalVisible,
          is2FAModalVisible,
          setIs2FAModalVisible,
          createForm,
          editForm,
          handleCreateUser,
          handleUpdateUser,
          handleDeleteUser,
          handleToggle2FA,
          handleGenerateBackupCodes,
          handleUserDetailsClick,
          fetchUsers,
          handleRefresh,
          handleExportData,
          handleSuspendUser
        }}
      />
      <BackupCodesModal
        isOpen={isBackupCodesOpen}
        onClose={() => setIsBackupCodesOpen(false)}
        backupCodes={backupCodes}
      />
    </Layout1>
  );
};

export default Index;